/**
 * Settings → engagement cadences (guide §5.7 / §6.7, ADR-001 §11.8): the eight activities with their name, the
 * days between two of them, the cadence for a new client (seeded only for training, 15), and on/off. The
 * prototype has no settings screen for this (its cadences are fixed in `ENGA`), so it is drawn with the
 * prototype's Settings panel pieces: `.panel` rows, `.in` inputs and the `tgl()` switch.
 *
 * Data: GET /engagement/activities; PATCH /engagement/activities/{key} (founder and associate director —
 * `settings.write`; everyone else sees the list read-only). A change applies on the next read of the grid.
 */
import { useState } from 'react';
import type { EngagementActivity, UpdateEngagementActivityBody } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { useEngagementActivities, useUpdateActivity } from '../../lib/engagement';
import { useSession } from '../../lib/session';
import { Empty, ErrorState, Loading, Switch, errorText, useToast } from '../../ui/kit';

export function EngagementCadences() {
  const { can } = useSession();
  const edit = can('settings.write');
  const q = useEngagementActivities();

  return (
    <div className="panel">
      <div className="hd">
        <h3>Engagement cadences</h3>
        <div className="r">
          <span className="sub">Days between two of each, for every client</span>
        </div>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <div className="bd">
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </div>
      ) : q.data?.data.length ? (
        <>
          <div className="bd" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 620 }}>
              <thead>
                <tr>
                  <th>Activity</th>
                  <th className="tr">Every (days)</th>
                  <th className="tr">New client (days)</th>
                  <th>On</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {q.data.data.map((a) => (
                  <Row key={a.key} a={a} edit={edit} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="bd sub" style={{ borderTop: '1px solid var(--line)', fontSize: 12.5 }}>
            A new client is one that started this year. Leave “New client” empty to use the same cadence. An activity switched off leaves the grid, the brief and “Add what is due”.
            {edit ? '' : ' Only the founder and the Ops associate director can change these.'}
          </div>
        </>
      ) : (
        <div className="bd">
          <Empty title="No engagement activities">The eight activities appear here once the workspace is set up.</Empty>
        </div>
      )}
    </div>
  );
}

function Row({ a, edit }: { a: EngagementActivity; edit: boolean }) {
  const upd = useUpdateActivity();
  const toast = useToast();
  const [name, setName] = useState(a.name);
  const [cad, setCad] = useState(String(a.cadence_days));
  const [nc, setNc] = useState(a.new_client_cadence_days == null ? '' : String(a.new_client_cadence_days));
  const [err, setErr] = useState<string | null>(null);

  const body: UpdateEngagementActivityBody = {};
  if (name.trim() !== a.name) body.name = name.trim();
  if (Number(cad) !== a.cadence_days) body.cadence_days = Number(cad);
  const ncVal = nc.trim() === '' ? null : Number(nc);
  if (ncVal !== a.new_client_cadence_days) body.new_client_cadence_days = ncVal;
  const dirty = Object.keys(body).length > 0;

  const send = (b: UpdateEngagementActivityBody, done: string) => {
    setErr(null);
    upd.mutate(
      { key: a.key, body: b },
      {
        onSuccess: () => toast(done),
        onError: (e) => {
          const fe = e instanceof ApiFailure ? Object.values(e.fieldErrors)[0] : undefined;
          setErr(fe ?? errorText(e));
          toast(fe ?? errorText(e));
        },
      },
    );
  };
  const save = () => {
    const bad = (v: number) => !Number.isInteger(v) || v < 1 || v > 365;
    if (name.trim().length < 2) return setErr('Give the activity a name of at least 2 characters');
    if (bad(Number(cad))) return setErr('A cadence is a whole number of days, 1 to 365');
    if (ncVal != null && bad(ncVal)) return setErr('A cadence is a whole number of days, 1 to 365');
    send(body, `${name.trim()} saved. The grid uses it from now on.`);
  };

  return (
    <tr>
      <td style={{ minWidth: 220 }}>
        {edit ? <input className="in" value={name} onChange={(e) => setName(e.target.value)} aria-label={`Name of ${a.name}`} /> : <b>{a.name}</b>}
        <div className="sub" style={{ marginTop: 3 }}>
          {a.beat}
          {a.note_required ? ' · needs a note when logged' : ''}
        </div>
        {err ? <small className="ferr">{err}</small> : null}
      </td>
      <td className="tr">
        {edit ? <input className="in mins" type="number" min={1} max={365} value={cad} onChange={(e) => setCad(e.target.value)} aria-label={`Days between two of ${a.name}`} /> : <span className="num">{a.cadence_days}</span>}
      </td>
      <td className="tr">
        {edit ? (
          <input className="in mins" type="number" min={1} max={365} placeholder="Same" value={nc} onChange={(e) => setNc(e.target.value)} aria-label={`Days for a new client, ${a.name}`} />
        ) : (
          <span className="num">{a.new_client_cadence_days ?? '-'}</span>
        )}
      </td>
      <td>
        <Switch on={a.active} disabled={!edit || upd.isPending} label={`${a.name} on`} onClick={() => send({ active: !a.active }, `${a.name} switched ${a.active ? 'off' : 'on'}`)} />
      </td>
      <td className="tr">
        {edit ? (
          <button className="btn sm pri" disabled={!dirty || upd.isPending} onClick={save}>
            Save
          </button>
        ) : null}
      </td>
    </tr>
  );
}
